<template>
  <div class="page-wrapper">
    <div class="page-toolbar">
      <div class="page-toolbar-left">
        <div class="filter-tabs">
          <button v-for="tab in filterTabs" :key="tab.v" type="button" class="filter-tab"
            :class="{ active: filterTab === tab.v }" @click="filterTab = tab.v">{{ tab.l }}</button>
        </div>
        <div class="search-box">
          <el-icon><Search /></el-icon>
          <input v-model="searchQuery" type="text" placeholder="搜索器材名称、品牌" />
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <el-icon class="loading-icon"><Loading /></el-icon>
      <p>加载中...</p>
    </div>

    <div v-else-if="filteredList.length === 0" class="empty-state">
      <el-icon><Tools /></el-icon>
      <p>暂无器材</p>
    </div>

    <div v-else class="equip-grid">
      <div class="equip-card" v-for="item in filteredList" :key="item.equipmentId">
        <div class="equip-card-media" :class="'status-' + item.status">
          <img
            v-if="item._coverUrl && !coverLoadFailed[item.equipmentId]"
            :src="item._coverUrl"
            class="equip-card-img"
            :alt="item.name"
            @error="onCoverError(item.equipmentId)"
          />
          <el-icon v-else class="equip-card-fallback-icon"><Tools /></el-icon>
        </div>
        <div class="equip-card-body">
          <div class="equip-header">
            <h3 class="equip-name">{{ item.name }}</h3>
            <span class="equip-status-badge" :class="'st-' + item.status">
              {{ item.status === 1 ? '正常' : '维修中' }}
            </span>
          </div>
          <p class="equip-brand">{{ item.brand || '-' }} · {{ item.model || '-' }}</p>
          <div class="equip-meta">
            <span><el-icon><Location /></el-icon>{{ item.location || '未知' }}</span>
            <span><el-icon><Calendar /></el-icon>维护：{{ item.lastMaintenance || '暂无' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Search, Tools, Calendar, Loading } from '@element-plus/icons-vue';
import { Location } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import api from '@/api/request';
import { equipmentCoverUrl } from '@/utils/media';

export default {
  name: 'MemberEquipmentView',
  components: { Search, Tools, Calendar, Location, Loading },
  data() {
    return {
      loading: false,
      filterTab: 'all',
      filterTabs: [
        { v: 'all', l: '全部' },
        { v: '1', l: '正常使用' },
        { v: '0', l: '维修中' },
      ],
      searchQuery: '',
      equipments: [],
      coverLoadFailed: {},
    };
  },
  computed: {
    filteredList() {
      let list = this.equipments;
      if (this.filterTab !== 'all') {
        const s = Number(this.filterTab);
        list = list.filter(e => e.status === s);
      }
      const q = (this.searchQuery || '').trim();
      if (!q) {
        return list.map(e => ({
          ...e,
          _coverUrl: equipmentCoverUrl(e),
        }));
      }
      return list
        .filter(e =>
          (e.name && e.name.includes(q)) ||
          (e.brand || '').includes(q)
        )
        .map(e => ({
          ...e,
          _coverUrl: equipmentCoverUrl(e),
        }));
    },
  },
  mounted() {
    this.loadEquipments();
  },
  methods: {
    onCoverError(equipmentId) {
      if (equipmentId == null) return;
      this.coverLoadFailed = { ...this.coverLoadFailed, [equipmentId]: true };
    },
    async loadEquipments() {
      this.loading = true;
      this.coverLoadFailed = {};
      try {
        const res = await api.getEquipmentList();
        if (res.data && res.data.code === 200) {
          this.equipments = res.data.data || [];
        }
      } catch (e) {
        ElMessage.error('加载器材列表失败');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.page-wrapper { padding: 0; background: var(--bg-base); min-height: 100vh; }
.page-toolbar { display: flex; align-items: center; margin-bottom: 20px; gap: 12px; flex-wrap: wrap; }
.page-toolbar-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; flex: 1; }
.filter-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
.filter-tab { padding: 6px 16px; border: 1px solid var(--border-color); background: var(--bg-gray); color: var(--text-secondary); border-radius: var(--radius-sm); font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.3s; }
.filter-tab:hover { border-color: var(--primary-blue); color: var(--primary-blue); }
.filter-tab.active { background: var(--primary-blue); border-color: var(--primary-blue); color: white; }
.search-box { display: flex; align-items: center; background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-md); padding: 0 12px; height: 38px; min-width: 200px; gap: 8px; transition: all 0.3s; }
.search-box:focus-within { border-color: var(--primary-blue); box-shadow: 0 0 0 3px rgba(0,98,255,0.08); }
.search-box .el-icon { color: var(--text-muted); }
.search-box input { border: none; background: transparent; color: var(--text-primary); font-size: 14px; outline: none; flex: 1; }
.search-box input::placeholder { color: var(--text-muted); }
.loading-state, .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; color: var(--text-muted); gap: 12px; font-size: 15px; }
.loading-state .el-icon, .empty-state .el-icon { font-size: 48px; }
.loading-icon { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.equip-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
.equip-card { background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-lg); padding: 20px; display: flex; gap: 16px; align-items: flex-start; transition: all 0.3s; }
.equip-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }
.equip-card-media { width: 72px; height: 72px; border-radius: var(--radius-md); display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden; border: 1px solid var(--border-color); }
.equip-card-media.status-1 { background: rgba(16,185,129,0.06); }
.equip-card-media.status-0 { background: rgba(245,158,11,0.06); }
.equip-card-img { width: 100%; height: 100%; object-fit: cover; }
.equip-card-fallback-icon { font-size: 28px; color: var(--text-muted); }
.equip-card-body { flex: 1; min-width: 0; }
.equip-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.equip-name { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0; }
.equip-status-badge { padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.equip-status-badge.st-1 { background: rgba(16,185,129,0.1); color: #10B981; }
.equip-status-badge.st-0 { background: rgba(245,158,11,0.1); color: #F59E0B; }
.equip-brand { font-size: 12px; color: var(--text-muted); margin: 0 0 10px; overflow-wrap: anywhere; }
.equip-meta { display: flex; flex-direction: column; gap: 4px; }
.equip-meta span { font-size: 12px; color: var(--text-secondary); display: flex; align-items: center; gap: 4px; overflow-wrap: anywhere; }

@media (max-width: 768px) {
  .page-toolbar-left { flex-direction: column; align-items: stretch; }
  .filter-tabs { overflow-x: auto; -webkit-overflow-scrolling: touch; flex-wrap: nowrap; padding-bottom: 4px; }
  .filter-tab { flex-shrink: 0; white-space: nowrap; }
  .search-box { min-width: 0; max-width: none; width: 100%; }
}

@media (max-width: 480px) {
  .equip-grid { grid-template-columns: 1fr; gap: 12px; }
}
</style>
